import styled, { css } from 'styled-components';
import { InputVariant, InputSize } from './types';

const wrapperVariantStyles = {
  default: css`
    border-color: ${({ theme }) => theme.colors.border};

    &:hover {
      border-color: ${({ theme }) => theme.colors.borderStrong};
    }

    &:focus-within {
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: ${({ theme }) => theme.shadows.focusRing(theme.colors.primary)};
    }
  `,
  error: css`
    border-color: ${({ theme }) => theme.colors.danger};
    
    &:focus-within {
      border-color: ${({ theme }) => theme.colors.danger};
      box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.danger};
    }
  `,
  success: css`
    border-color: ${({ theme }) => theme.colors.success || '#10b981'};
    
    &:focus-within {
      border-color: ${({ theme }) => theme.colors.success || '#10b981'};
      box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.success || '#10b981'};
    }
  `,
};

const sizeStyles = {
  default: css`
    height: 3rem;
    padding: 0 ${({ theme }) => theme.spacing[4]};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  `,
  sm: css`
    height: 2.25rem;
    padding: 0 ${({ theme }) => theme.spacing[3]};
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  `,
  lg: css`
    height: 3.5rem;
    padding: 0 ${({ theme }) => theme.spacing[5]};
    font-size: ${({ theme }) => theme.typography.fontSize.md};
  `,
};

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  overflow: visible;
`;

export const Label = styled.label<{ $labelStyle?: 'default' | 'outlined' }>`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.gray500};
  //margin-bottom: ${({ theme }) => theme.spacing[2]};
  transition: all 0.2s ease-in-out;

  ${({ $labelStyle }) => $labelStyle === 'outlined' && css`
    position: absolute;
    top: -0.625rem;
    left: 0.75rem;
    background: ${({ theme }) => theme.colors.surface};
    padding: 0 0.25rem;
    font-size: 0.75rem;
    z-index: 1;
    margin-bottom: 0;
    color: ${({ theme }) => theme.colors.gray400};
  `}
`;

export const InputWrapper = styled.div<{ $variant: InputVariant; $size: InputSize; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.surface};
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  overflow: visible;

  ${({ $variant }) => wrapperVariantStyles[$variant]}
  
  ${({ $size }) => css`
    height: ${$size === 'sm' ? '2.25rem' : $size === 'lg' ? '3.5rem' : '3rem'};
  `}
  
  ${({ $disabled }) => $disabled && css`
    background-color: ${({ theme }) => theme.colors.gray100};
    cursor: not-allowed;
    opacity: 0.6;
  `}
`;

export const AdornmentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  color: ${({ theme }) => theme.colors.gray500};
  height: 100%;
`;

export const StyledInput = styled.input<{
  $size: InputSize;
  $hasStartAdornment?: boolean;
  $hasEndAdornment?: boolean;
}>`
  flex: 1;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.gray800};
  width: 100%;
  line-height: normal;
  display: flex;
  align-items: center;

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray400};
  }

  &:disabled {
    cursor: not-allowed;
  }

  ${({ $size }) => sizeStyles[$size]}
  
  ${({ $hasStartAdornment }) => $hasStartAdornment && css`
    padding-left: ${({ theme }) => theme.spacing[1]};
  `}
  
  ${({ $hasEndAdornment }) => $hasEndAdornment && css`
    padding-right: ${({ theme }) => theme.spacing[1]};
  `}
`;

export const ErrorMessage = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.danger};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

export const HelperText = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray500};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;
